import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { messageApi, fileApi } from '../lib/api';
import {
  Send, MessageSquare, Loader2, ArrowLeft, Search, Mic, MicOff,
  Image as ImageIcon, Paperclip, X, Play, Pause, AlertTriangle,
  Check, CheckCheck, Download, Trash2, HardDrive
} from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';

const SAFETY_WARNING = `⚠️ For your safety, please keep all communications within Bookd. 
Sharing personal contact information (phone numbers, emails, social media) puts you at risk 
and makes it harder to resolve disputes. All conversations are saved for your reference.`;

// Voice note player component (moved outside to avoid re-renders)
const VoiceNotePlayer = ({ audioUrl, duration, formatDuration }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="flex items-center gap-2 bg-muted/50 rounded-full px-3 py-2">
      <audio ref={audioRef} src={audioUrl} onEnded={() => setIsPlaying(false)} />
      <button onClick={togglePlay} className="p-1">
        {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </button>
      <div className="w-24 h-1 bg-muted rounded-full">
        <div className="h-full bg-primary rounded-full w-0" />
      </div>
      <span className="text-xs text-muted-foreground">{formatDuration(duration || 0)}</span>
    </div>
  );
};

const Messages = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  
  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  
  // Attachments
  const [showAttachmentPreview, setShowAttachmentPreview] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Storage info
  const [storageInfo, setStorageInfo] = useState(null);
  const [showStorageWarning, setShowStorageWarning] = useState(false);
  
  // Safety warning
  const [showSafetyWarning, setShowSafetyWarning] = useState(false);
  const [pendingMessage, setPendingMessage] = useState(null);
  
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    loadConversations();
    
    const recipientId = searchParams.get('to');
    if (recipientId) {
      startNewConversation(recipientId);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.conversation_id);
      
      pollIntervalRef.current = setInterval(() => {
        loadMessages(selectedConversation.conversation_id, true);
      }, 5000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const response = await messageApi.getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const startNewConversation = async (recipientId) => {
    try {
      const response = await messageApi.startConversation(recipientId);
      setSelectedConversation({
        conversation_id: response.data.conversation_id,
        participant_id: response.data.participant.user_id,
        participant_name: response.data.participant.name,
        participant_picture: response.data.participant.picture,
        participant_type: response.data.participant.user_type
      });
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const loadMessages = async (conversationId, silent = false) => {
    if (!silent) setLoadingMessages(true);
    try {
      const response = await messageApi.getMessages(conversationId);
      setMessages(response.data);
      if (!silent) {
        loadConversations();
      }
    } catch (error) {
      if (!silent) {
        toast.error('Failed to load messages');
      }
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  };

  // Check for contact info before sending
  const checkAndSendMessage = async (messageData) => {
    if (messageData.message_type === 'text' && messageData.content) {
      try {
        const checkResponse = await messageApi.checkContact(messageData.content);
        if (checkResponse.data.has_contact_info) {
          setPendingMessage(messageData);
          setShowSafetyWarning(true);
          return;
        }
      } catch (error) {
        // If check fails, proceed anyway
      }
    }
    await sendMessageToServer(messageData);
  };

  const sendMessageToServer = async (messageData) => {
    setSendingMessage(true);
    try {
      const response = await messageApi.sendMessage(messageData);
      
      // Show safety warning if returned
      if (response.data.has_contact_warning) {
        toast.warning('Message sent, but please avoid sharing contact details for your safety.', {
          duration: 5000
        });
      }
      
      setNewMessage('');
      setAudioBlob(null);
      setAttachmentFile(null);
      setAttachmentPreviewUrl(null);
      loadMessages(selectedConversation.conversation_id, true);
      loadConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
      setShowSafetyWarning(false);
      setPendingMessage(null);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    await checkAndSendMessage({
      recipient_id: selectedConversation.participant_id,
      content: newMessage.trim(),
      message_type: 'text'
    });
  };

  const proceedWithMessage = async () => {
    if (pendingMessage) {
      await sendMessageToServer(pendingMessage);
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setRecordingTime(0);
  };

  const sendVoiceNote = async () => {
    if (!audioBlob || !selectedConversation) return;

    setUploadingFile(true);
    try {
      // Upload voice note file to server
      const uploadResponse = await fileApi.uploadVoice(new File([audioBlob], `voice_${Date.now()}.webm`, { type: 'audio/webm' }));
      
      // Check if storage limit reached
      if (uploadResponse.data.storage_used_mb >= uploadResponse.data.storage_limit_mb * 0.9) {
        setStorageInfo(uploadResponse.data);
        setShowStorageWarning(true);
      }
      
      // Send message with file URL
      await sendMessageToServer({
        recipient_id: selectedConversation.participant_id,
        content: '🎤 Voice note',
        message_type: 'voice',
        attachment_url: uploadResponse.data.url,
        voice_duration: recordingTime
      });
      
      setAudioBlob(null);
      setRecordingTime(0);
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Storage limit')) {
        setShowStorageWarning(true);
        toast.error('Storage limit reached. Please download and delete old voice notes.');
      } else {
        toast.error('Failed to send voice note');
      }
    } finally {
      setUploadingFile(false);
    }
  };

  // File attachment functions
  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (type === 'image' && !file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error('File size must be less than 10MB');
      return;
    }

    setAttachmentFile(file);
    
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setAttachmentPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreviewUrl(null);
    }
    
    setShowAttachmentPreview(true);
  };

  const sendAttachment = async () => {
    if (!attachmentFile || !selectedConversation) return;

    setUploadingFile(true);
    try {
      // Upload file to server
      const uploadResponse = await fileApi.uploadAttachment(attachmentFile);
      const isImage = uploadResponse.data.file_type === 'image';
      
      // Send message with file URL
      await sendMessageToServer({
        recipient_id: selectedConversation.participant_id,
        content: isImage ? '📷 Image' : `📎 ${attachmentFile.name}`,
        message_type: isImage ? 'image' : 'file',
        attachment_url: uploadResponse.data.url,
        attachment_name: attachmentFile.name
      });
      
      setShowAttachmentPreview(false);
      setAttachmentFile(null);
      setAttachmentPreviewUrl(null);
    } catch (error) {
      toast.error('Failed to send attachment');
    } finally {
      setUploadingFile(false);
    }
  };

  // Search
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      const response = await messageApi.searchMessages(searchQuery, selectedConversation?.conversation_id);
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      return 'Yesterday ' + format(date, 'h:mm a');
    }
    return format(date, 'MMM d, h:mm a');
  };

  const formatConversationTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isToday(date)) {
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      return 'Yesterday';
    }
    return format(date, 'MMM d');
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper to build full URL for attachments
  const getFullUrl = (url) => {
    if (!url) return '';
    // If already a full URL or base64, return as is
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    // Build full API URL
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16" data-testid="messages-page">
      <div className="max-w-7xl mx-auto h-[calc(100vh-4rem)]">
        <div className="grid md:grid-cols-3 h-full">
          {/* Conversations List */}
          <div className={`border-r border-border ${selectedConversation ? 'hidden md:block' : ''}`}>
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between mb-3">
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Messages
                </h1>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowSearch(!showSearch)}
                >
                  <Search className="h-5 w-5" />
                </Button>
              </div>
              
              {showSearch && (
                <div className="flex gap-2">
                  <Input
                    placeholder="Search messages..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="rounded-full"
                    data-testid="message-search-input"
                  />
                  <Button size="icon" onClick={handleSearch} className="rounded-full">
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Search Results */}
            {searchResults && (
              <div className="p-3 bg-muted/30 border-b border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{searchResults.count} results</span>
                  <Button variant="ghost" size="sm" onClick={() => setSearchResults(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <ScrollArea className="max-h-40">
                  {searchResults.results.map(msg => (
                    <div key={msg.message_id} className="p-2 text-sm hover:bg-muted/50 rounded cursor-pointer">
                      <p className="font-medium">{msg.sender_name}</p>
                      <p className="text-muted-foreground truncate">{msg.content}</p>
                    </div>
                  ))}
                </ScrollArea>
              </div>
            )}
            
            <ScrollArea className="h-[calc(100vh-12rem)]">
              {conversations.length > 0 ? (
                <div className="divide-y divide-border">
                  {conversations.map(conv => (
                    <button
                      key={conv.conversation_id}
                      onClick={() => setSelectedConversation(conv)}
                      className={`w-full p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left ${
                        selectedConversation?.conversation_id === conv.conversation_id ? 'bg-muted/50' : ''
                      }`}
                      data-testid={`conversation-${conv.conversation_id}`}
                    >
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={conv.participant_picture} />
                        <AvatarFallback className="bg-primary/20 text-primary">
                          {conv.participant_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium truncate">{conv.participant_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatConversationTime(conv.last_message_time)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-muted-foreground truncate">
                            {conv.last_message || 'No messages yet'}
                          </p>
                          {conv.unread_count > 0 && (
                            <Badge className="ml-2 bg-primary text-primary-foreground text-xs">
                              {conv.unread_count}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No conversations yet</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Start a conversation from a bookee's profile
                  </p>
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Chat Area */}
          <div className={`md:col-span-2 flex flex-col ${!selectedConversation ? 'hidden md:flex' : ''}`}>
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-border flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden"
                    onClick={() => setSelectedConversation(null)}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={selectedConversation.participant_picture} />
                    <AvatarFallback className="bg-primary/20 text-primary">
                      {selectedConversation.participant_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <h2 className="font-bold">{selectedConversation.participant_name}</h2>
                    <p className="text-xs text-muted-foreground capitalize">
                      {selectedConversation.participant_type === 'talent' ? 'Bookee' : 'Booker'}
                    </p>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : messages.length > 0 ? (
                    <div className="space-y-4">
                      {messages.map(message => {
                        const isOwn = message.sender_id === user?.user_id;
                        return (
                          <div
                            key={message.message_id}
                            className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`flex gap-2 max-w-[75%] ${isOwn ? 'flex-row-reverse' : ''}`}>
                              {!isOwn && (
                                <Avatar className="h-8 w-8 flex-shrink-0">
                                  <AvatarImage src={message.sender_picture} />
                                  <AvatarFallback className="text-xs">
                                    {message.sender_name?.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                              )}
                              <div>
                                {/* Contact warning indicator */}
                                {message.has_contact_warning && (
                                  <div className="flex items-center gap-1 text-yellow-500 text-xs mb-1">
                                    <AlertTriangle className="h-3 w-3" />
                                    <span>Contains contact info</span>
                                  </div>
                                )}
                                
                                <div
                                  className={`px-4 py-2 rounded-2xl ${
                                    isOwn
                                      ? 'bg-primary text-primary-foreground rounded-br-md'
                                      : 'bg-muted rounded-bl-md'
                                  }`}
                                >
                                  {message.message_type === 'voice' && message.attachment_url ? (
                                    <VoiceNotePlayer 
                                      audioUrl={getFullUrl(message.attachment_url)} 
                                      duration={message.voice_duration}
                                      formatDuration={formatDuration}
                                    />
                                  ) : message.message_type === 'image' && message.attachment_url ? (
                                    <img 
                                      src={getFullUrl(message.attachment_url)} 
                                      alt="Shared image" 
                                      className="max-w-[200px] rounded-lg cursor-pointer"
                                      onClick={() => window.open(getFullUrl(message.attachment_url), '_blank')}
                                    />
                                  ) : message.message_type === 'file' && message.attachment_url ? (
                                    <a 
                                      href={getFullUrl(message.attachment_url)} 
                                      download={message.attachment_name}
                                      className="flex items-center gap-2 text-sm underline"
                                    >
                                      <Paperclip className="h-4 w-4" />
                                      {message.attachment_name || 'Download file'}
                                    </a>
                                  ) : (
                                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                                  )}
                                </div>
                                
                                <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                                  <p className="text-xs text-muted-foreground">
                                    {formatMessageTime(message.created_at)}
                                  </p>
                                  {/* Read receipts */}
                                  {isOwn && (
                                    <span className="text-xs">
                                      {message.is_read ? (
                                        <CheckCheck className="h-3 w-3 text-primary" />
                                      ) : (
                                        <Check className="h-3 w-3 text-muted-foreground" />
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">No messages yet</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Send a message to start the conversation
                        </p>
                      </div>
                    </div>
                  )}
                </ScrollArea>

                {/* Voice Recording UI */}
                {(isRecording || audioBlob) && (
                  <div className="px-4 py-3 border-t border-border bg-muted/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {isRecording ? (
                          <>
                            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                            <span className="text-sm font-medium">Recording... {formatDuration(recordingTime)}</span>
                          </>
                        ) : (
                          <>
                            <Mic className="h-5 w-5 text-primary" />
                            <span className="text-sm">Voice note ready ({formatDuration(recordingTime)})</span>
                          </>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={cancelRecording}>
                          <X className="h-4 w-4 mr-1" /> Cancel
                        </Button>
                        {isRecording ? (
                          <Button size="sm" onClick={stopRecording} className="rounded-full">
                            <MicOff className="h-4 w-4 mr-1" /> Stop
                          </Button>
                        ) : (
                          <Button size="sm" onClick={sendVoiceNote} className="rounded-full btn-glow">
                            <Send className="h-4 w-4 mr-1" /> Send
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Message Input */}
                <div className="p-4 border-t border-border">
                  <form onSubmit={handleSendMessage} className="flex gap-2 items-end">
                    {/* Attachment buttons */}
                    <div className="flex gap-1">
                      <input
                        type="file"
                        accept="image/*"
                        ref={imageInputRef}
                        onChange={(e) => handleFileSelect(e, 'image')}
                        className="hidden"
                      />
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={(e) => handleFileSelect(e, 'file')}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => imageInputRef.current?.click()}
                        data-testid="attach-image-btn"
                      >
                        <ImageIcon className="h-5 w-5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="attach-file-btn"
                      >
                        <Paperclip className="h-5 w-5" />
                      </Button>
                    </div>

                    <Input
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className="rounded-full flex-1"
                      disabled={sendingMessage || isRecording}
                      data-testid="message-input"
                    />

                    {/* Voice note button */}
                    {!newMessage.trim() && !isRecording && !audioBlob ? (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={startRecording}
                        className="rounded-full"
                        data-testid="voice-note-btn"
                      >
                        <Mic className="h-5 w-5" />
                      </Button>
                    ) : null}

                    <Button
                      type="submit"
                      size="icon"
                      className="rounded-full btn-glow"
                      disabled={!newMessage.trim() || sendingMessage}
                      data-testid="send-message-btn"
                    >
                      {sendingMessage ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Send className="h-5 w-5" />
                      )}
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-center p-8">
                <div>
                  <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                    Your Messages
                  </h2>
                  <p className="text-muted-foreground">
                    Select a conversation or start a new one from a bookee's profile
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Safety Warning Dialog */}
      <Dialog open={showSafetyWarning} onOpenChange={setShowSafetyWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-500">
              <AlertTriangle className="h-5 w-5" />
              Safety Warning
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground whitespace-pre-line">{SAFETY_WARNING}</p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowSafetyWarning(false)}>
              Edit Message
            </Button>
            <Button onClick={proceedWithMessage} className="bg-yellow-600 hover:bg-yellow-700">
              Send Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Attachment Preview Dialog */}
      <Dialog open={showAttachmentPreview} onOpenChange={setShowAttachmentPreview}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Attachment</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {attachmentPreviewUrl ? (
              <img src={attachmentPreviewUrl} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
            ) : (
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                <Paperclip className="h-8 w-8" />
                <span>{attachmentFile?.name}</span>
              </div>
            )}
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowAttachmentPreview(false)}>
              Cancel
            </Button>
            <Button onClick={sendAttachment} disabled={uploadingFile} className="btn-glow">
              {uploadingFile ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Storage Warning Dialog */}
      <Dialog open={showStorageWarning} onOpenChange={setShowStorageWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-500">
              <HardDrive className="h-5 w-5" />
              Storage Limit Approaching
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-muted-foreground">
              You've used {storageInfo?.storage_used_mb?.toFixed(1)}MB of {storageInfo?.storage_limit_mb}MB voice note storage.
            </p>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-yellow-500" 
                style={{ width: `${Math.min((storageInfo?.storage_used_mb / storageInfo?.storage_limit_mb) * 100, 100)}%` }}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              To free up space, download your voice notes and delete old ones from your storage settings.
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowStorageWarning(false)}>
              Dismiss
            </Button>
            <Button onClick={async () => {
              const response = await fileApi.downloadAllVoice();
              if (response.data.files.length > 0) {
                response.data.files.forEach(file => {
                  const link = document.createElement('a');
                  link.href = getFullUrl(file.url);
                  link.download = file.original_name;
                  link.click();
                });
                toast.success(`Downloading ${response.data.files.length} voice notes`);
              }
              setShowStorageWarning(false);
            }}>
              <Download className="h-4 w-4 mr-2" />
              Download All
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
